import { useMemo } from "react";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modifydetail,
  PracticeHistory,
  ReadOnlyPractice,
  useLazyGetPracticeHistoryQuery,
} from "../../../../../redux/apiSpecifications/apiCrud";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import {
  RefreshInTabWrapper,
  WidgetWrapper,
} from "../../../../../style/DashbordWidgetWrappers";
import { mainTabs } from "../../../../../config/testIds";
import ModifyDescription from "./ModifyDescription/ModifyDescription";
import { dateToDisplayDate } from "../../../../../utils/date";
import { RootState } from "../../../../../redux/store";
import { useSelector } from "react-redux";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import Loading from "../../../../Loading";

const testIds = mainTabs.tabs.historicalActions;

const ContentWrapper = styled.div`
  position: relative;

  .positionLoading {
    position: absolute;
    top: 30%;
    left: 30%;
    zindex: 999;
  }
`;

const ListActions = styled(WidgetWrapper)`
  align-items: flex-start;
  max-width: 100%;
  overflow: auto;
  padding: 2em;

  .MuiPaper-root {
    box-shadow: none;
    width: 100%;
  }

  .MuiToolbar-root > .MuiBox-root {
    justify-content: flex-start;
  }

  .MuiTableCell-root {
    padding: 0;
  }
`;

const CellWrapper = styled.div`
  cursor: pointer;
  padding: 1em 0;
`;

interface IAppraisalDetailsHistoricalActionsProps {
  practice: ReadOnlyPractice | undefined;
  defaultActiveKey?: string[];
  isActive?: boolean;
  activeKey?: string;
}

type PracticeHistoryTableRow = {
  modifyType?: string;
  modifyDescription?: string;
  modifyUser?: string;
  modifyTimestamp: string;
  modifyDetailsJson?: Modifydetail[];
};

const AppraisalDetailsHistoricalActions = (
  props: IAppraisalDetailsHistoricalActionsProps
) => {
  const { t } = useTranslation();

  const { language, authorization, activeRole } =
    useSelector((state: RootState) => state.user) || {};

  const { practice, isActive, activeKey } = props;

  const [historical, setHistorical] = useState<PracticeHistory[]>([]);
  const [retrievePracticeHistory] = useLazyGetPracticeHistoryQuery();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    loadData();
  }, [practice?.appraiser, isActive]);

  useEffect(() => {
    if (activeKey === "historical") {
      loadData();
    }
  }, [activeKey]);

  const loadData = async () => {
    setIsLoading(true);
    const { data, error, isSuccess } = await retrievePracticeHistory({
      authorization,
      idPractice: practice?.id as number,
      activeRole: activeRole!,
    });

    if (isSuccess && data) {
      setHistorical(data as PracticeHistory[]);
    } else {
      // Gestione errore se necessario
    }
    setIsLoading(false);
  };

  const dataHistorical = useMemo(
    () =>
      historical?.map(
        (item: PracticeHistory) =>
          ({
            modifyTimestamp: item.modifyTimestamp,
            modifyUser: item.modifyUser,
            modifyDescription: item.modifyDescription,
            modifyType: item.modifyType,
            modifyDetailsJson: item.modifyDetailsJson,
          } as PracticeHistoryTableRow)
      ) || [],
    [historical]
  );

  const resultsColumns = useMemo<MRT_ColumnDef<PracticeHistoryTableRow>[]>(
    () => [
      {
        accessorKey: "modifyTimestamp",
        header: t("appraisal-historical-tab-date"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>
            {dateToDisplayDate(row.original.modifyTimestamp)}
          </CellWrapper>
        ),
      },
      {
        accessorKey: "modifyUser",
        header: t("appraisal-historical-tab-user"),
        size: 100,
        Cell: ({ row }) => <CellWrapper>{row.original.modifyUser}</CellWrapper>,
      },
      {
        accessorKey: "modifyDescription",
        header: t("appraisal-historical-tab-description"),
        size: 300,
        Cell: ({ row }) => (
          <CellWrapper>
            <ModifyDescription item={row.original} />
          </CellWrapper>
        ),
      },
    ],
    [historical, language]
  );

  return (
    <>
      {isLoading && (
        <RefreshInTabWrapper
          className="positionLoading"
          data-testid={testIds.loadingContainer}
        >
          <Loading />
        </RefreshInTabWrapper>
      )}

      <ContentWrapper data-testid={testIds.container}>
        <ListActions data-testid={testIds.table}>
          <MaterialReactTable
            columns={resultsColumns}
            data={dataHistorical}
            enableDensityToggle={false}
            enableColumnOrdering
            localization={
              language === "it" ? MRT_Localization_IT : MRT_Localization_EN
            }
            muiTableBodyCellProps={{
              sx: {
                textAlign: "left",
              },
            }}
            onPaginationChange={setPagination}
            state={{ pagination }}
          />
        </ListActions>
      </ContentWrapper>
    </>
  );
};

export default AppraisalDetailsHistoricalActions;
